import React, { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider/src/ModalContextProvider.tsx';
import { fireOperationalAnalytics, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useCanDownloadAttachments } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import messages from '../messages.tsx';
import DeleteAllItem from './delete-all-item/index.tsx';
import DownloadAllItem from './download-all-item/index.tsx';
import content from './messages.tsx';
import SwitchViewItem from './switch-view-item/index.tsx';
import type { Props } from './types.tsx';

const MeatBalls = ({ refreshAttachments, isPreview, defaultOpen = false }: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);

	const isDownloadSupported = useCanDownloadAttachments(projectKey);
	const renderDropdownMenuButton = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'triggerRef' implicitly has an 'any' type.
		({ triggerRef, ...triggerProps }) => (
			<Button
				{...triggerProps}
				appearance="subtle"
				spacing="compact"
				iconBefore={
					<MoreIcon
						color="currentColor"
						spacing="none"
						LEGACY_size="small"
						label={formatMessage(messages.attachmentsAriaLabel)}
					/>
				}
				isDisabled={isPreview}
				ref={triggerRef}
				interactionName="auto-generated.jira-issue-view-base.content.attachment.heading.meatballs.button"
			/>
		),
		[formatMessage, isPreview],
	);

	const dropdownMenu = (
		<DropdownMenu
			defaultOpen={defaultOpen}
			placement="bottom-end"
			trigger={renderDropdownMenuButton}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.content.attachment.heading.meatballs.drop-down"
		>
			{!isDownloadSupported && fg('block_attachment_download_on_data_export_dsp_rule') ? (
				<DropdownItemGroup>
					<SwitchViewItem refreshAttachments={refreshAttachments} />
					<DeleteAllItem />
					<Box xcss={dividerStyles} />
					<Box xcss={disclaimerStyles}>{formatMessage(content.downloadBlockDisclaimer)}</Box>
				</DropdownItemGroup>
			) : (
				<DropdownItemGroup>
					<SwitchViewItem refreshAttachments={refreshAttachments} />
					<DownloadAllItem />
					<DeleteAllItem />
				</DropdownItemGroup>
			)}
		</DropdownMenu>
	);
	return (
		<>
			<ModalContextProvider>{dropdownMenu}</ModalContextProvider>
			<MountEvent
				onMount={(analyticsEvent: UIAnalyticsEvent) =>
					fireOperationalAnalytics(analyticsEvent, 'AttachmentListViewOnboarding mounted')
				}
			/>
		</>
	);
};

const dividerStyles = xcss({
	height: '1px',
	width: '100%',
	backgroundColor: 'color.background.neutral',
	marginBlock: 'space.100',
});

const disclaimerStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.150',
	maxWidth: '192px',
	font: token('font.body.small'),
	color: 'color.text.subtle',
});

export default MeatBalls;
